import { useEffect, useRef, useState } from "react";
import StreamVideo, { hasTrailer } from "@/components/StreamVideo";
import AiFilmsRow from "@/components/AiFilmsRow";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { API, naira } from "@/lib/api";
import Navbar from "@/components/Navbar";
import LaunchBanner from "@/components/LaunchBanner";
import InstallPromptChip from "@/components/InstallPromptChip";
import IosInstallHint from "@/components/IosInstallHint";
import ChromeIosInstallHint from "@/components/ChromeIosInstallHint";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { toast } from "sonner";
import FilmCard from "@/components/FilmCard";
import { AiBadge } from "@/components/AiBadge";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Film, Sparkles, Award, CreditCard, Play, ArrowRight, Gift, Star, Trophy, ChevronLeft, ChevronRight, Info, Plus, Clock, Volume2, VolumeX, Mail, Phone, Smartphone, Download, Check } from "lucide-react";

const HERO_ROTATE_MS = 9000; // slightly longer so trailers can breathe
// The static launch poster sits full-cover on top of the trailer stack for the
// first ``HERO_STATIC_DELAY_MS`` after page load. Once it fades out, the
// trailer carousel takes over and auto-advances every ``HERO_ROTATE_MS``.
const HERO_STATIC_DELAY_MS = 9000;
// Local optimised hero assets — AVIF is ~84KB, WebP ~138KB, vs the 2MB
// PNG original that lives on the CDN. We serve AVIF first, WebP as
// fallback, and the CDN PNG as a last-ditch fallback for legacy browsers
// that support neither (currently <3% globally). Sized landscape for
// desktop/tablet (≥768px) and portrait for phones.
const HERO_DESKTOP = {
  avif: "/hero/hero-desktop.avif",
  webp: "/hero/hero-desktop.webp",
  png: "https://customer-assets-39nsmqrw.emergentagent.net/job_filmmakers-africa/artifacts/bhlssw0j_ChatGPT%20Image%20Sep%2019%2C%202026%2C%2010_29_45%20AM.png",
};
const HERO_MOBILE = {
  avif: "/hero/hero-mobile.avif",
  webp: "/hero/hero-mobile.webp",
  png: "https://customer-assets-39nsmqrw.emergentagent.net/job_filmmakers-africa/artifacts/1763umk5_ChatGPT%20Image%20Sep%2019%2C%202026%2C%2010_31_22%20AM.png",
};
// Words that cycle under the poster while the static hero is up, giving
// the section a heartbeat before the trailer carousel takes over. Order
// mirrors the artwork's stacked headline.
const HERO_ROTATING_WORDS = ["Real Stories.", "Bold Voices.", "Africa."];
const HERO_WORD_MS = 1600;

export default function Landing() {
  const [films, setFilms] = useState([]);
  const [hero, setHero] = useState([]);
  const [spotlight, setSpotlight] = useState({ month: "", films: [] });
  const [premiering, setPremiering] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [referral, setReferral] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [heroInView, setHeroInView] = useState(true);
  const [paused, setPaused] = useState(false); // pause auto-rotate on hover
  // Static launch poster covers the trailer stack for the first 9s. Once
  // ``heroRevealed`` flips true, the trailer carousel takes over and starts
  // rotating.
  const [heroRevealed, setHeroRevealed] = useState(false);
  // Rotating word under the poster. Advances every ``HERO_WORD_MS``
  // until the poster fades out, then freezes on the last word.
  const [heroWordIdx, setHeroWordIdx] = useState(0);
  const heroRef = useRef(null);
  const videoRefs = useRef([]);
  // Shared PWA install controller — powers the mobile-app section button
  // below with the exact same logic as the floating install chip.
  const { canInstall, isStandalone, platform, install } = usePwaInstall();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    // Cache-bust so newly-approved films show up immediately rather than
    // surfacing a stale CDN/browser response.
    const ts = Date.now();
    api.get(`/films/featured?_=${ts}`).then((r) => setFilms(r.data || [])).catch(() => {});
    api.get(`/films/hero?_=${ts}`).then((r) => setHero(r.data || [])).catch(() => {});
    api.get(`/films/spotlight?_=${ts}`).then((r) => setSpotlight(r.data)).catch(() => {});
    api.get(`/films/premiering?_=${ts}`).then((r) => setPremiering(r.data?.films || [])).catch(() => {});
    api.get(`/films/coming-soon?_=${ts}`).then((r) => setComingSoon(r.data?.films || [])).catch(() => {});

    const code = params.get("ref") || localStorage.getItem("cv_referral");
    if (code) {
      api.get(`/referrals/lookup/${code}`).then((r) => {
        localStorage.setItem("cv_referral", code);
        setReferral(r.data);
      }).catch(() => {
        localStorage.removeItem("cv_referral");
      });
    }
  }, [params]);

  // Hero carousel: newest Public + Premiering films.
  const heroFilms = hero;
  const heroLen = heroFilms.length;

  // Pause the rotation interval while the user is hovering, the hero is
  // scrolled out of view, or the static launch poster is still showing.
  useEffect(() => {
    if (heroLen < 2 || paused || !heroInView || !heroRevealed) return;
    const id = setInterval(() => {
      setHeroIdx((i) => (i + 1) % heroLen);
    }, HERO_ROTATE_MS);
    return () => clearInterval(id);
  }, [heroLen, paused, heroInView, heroRevealed]);

  // Only the active slide's trailer should be playing. Pause every other one
  // so we never stream 5 videos in parallel on a phone. Also hold ALL videos
  // paused until the static launch poster finishes its 9s reveal.
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === heroIdx && heroInView && heroRevealed) {
        // currentTime reset so transitions always start at frame 0
        try { v.currentTime = 0; } catch { /* ignore */ }
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else {
        try { v.pause(); } catch { /* ignore */ }
      }
    });
  }, [heroIdx, heroLen, heroInView, heroRevealed]);

  // Kick off the static-poster → trailer handoff on mount. A single timeout is
  // enough; we don't need to reset on hover / focus / etc — once trailers are
  // revealed, we stay in trailer mode for the rest of the session.
  useEffect(() => {
    if (heroRevealed) return;
    const t = setTimeout(() => setHeroRevealed(true), HERO_STATIC_DELAY_MS);
    return () => clearTimeout(t);
  }, [heroRevealed]);

  // Cycle the rotating headline word while the static poster is up. Stops
  // as soon as ``heroRevealed`` flips (poster fades out with it).
  useEffect(() => {
    if (heroRevealed) return undefined;
    const id = setInterval(() => {
      setHeroWordIdx((i) => (i + 1) % HERO_ROTATING_WORDS.length);
    }, HERO_WORD_MS);
    return () => clearInterval(id);
  }, [heroRevealed]);

  // IntersectionObserver — kill video playback when the hero scrolls off-screen.
  useEffect(() => {
    if (!heroRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setHeroInView(entry.isIntersecting && entry.intersectionRatio > 0.1),
      { threshold: [0, 0.1, 0.5] }
    );
    obs.observe(heroRef.current);
    return () => obs.disconnect();
  }, []);

  const goPrev = () => setHeroIdx((i) => (i - 1 + heroLen) % heroLen);
  const goNext = () => setHeroIdx((i) => (i + 1) % heroLen);
  const current = heroFilms[heroIdx];

  return (
    <div className="min-h-screen bg-ink grain">
      <LaunchBanner />
      <Navbar />
      <InstallPromptChip />
      <IosInstallHint />
      <ChromeIosInstallHint />

      {referral && (
        <div className="fixed top-[72px] left-1/2 -translate-x-1/2 z-30 px-4 py-2.5 rounded-full bg-gold/15 backdrop-blur border border-gold/30 flex items-center gap-2 shadow-lg shadow-gold/10" data-testid="referral-banner-landing">
          <Gift className="w-4 h-4 text-gold" />
          <p className="text-xs sm:text-sm">
            <span className="text-gold font-display font-bold">₦{referral.credit_naira} off</span>{" "}
            <span className="text-foreground/80">your first rental — referred by {referral.filmmaker_name}</span>
          </p>
        </div>
      )}

      {/* HERO — streaming-platform style with trailer-as-backdrop */}
      <section
        ref={heroRef}
        className="relative h-[92vh] min-h-[560px] sm:min-h-[640px] overflow-hidden"
        data-testid="landing-hero"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* STATIC LAUNCH POSTER — full-cover, sits on top of the trailer stack
            for the first ``HERO_STATIC_DELAY_MS``. Fades out at 900ms so it
            hands off smoothly to the first trailer. z-20 puts it above the
            backdrop stack (z-0/1) but below the foreground text/CTA layer
            (z-30) so title + buttons remain readable while it's showing.
            ``object-cover`` keeps the image filling the hero on every viewport
            without stretching. */}
        <div
          className={`absolute inset-0 z-20 transition-opacity duration-[900ms] ease-in-out ${
            heroRevealed ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          aria-hidden={heroRevealed}
          data-testid="hero-static-poster"
        >
          {/* Two image variants: a landscape composition for desktop/tablet
              (≥768px) and a portrait composition tailored for phones. The
              <picture> element lets the browser pick the right source at
              load time — no JS, no re-downloads when the viewport crosses
              the breakpoint after paint. Each source is already framed for
              its target orientation, so ``object-cover`` fills the hero
              edge-to-edge on every device without letterboxing. */}
          <picture>
            {/* Desktop (≥768px): AVIF → WebP → PNG fallback. Browsers
                pick the first source they can decode. */}
            <source media="(min-width: 768px)" type="image/avif" srcSet={HERO_DESKTOP.avif} />
            <source media="(min-width: 768px)" type="image/webp" srcSet={HERO_DESKTOP.webp} />
            <source media="(min-width: 768px)" type="image/png" srcSet={HERO_DESKTOP.png} />
            {/* Mobile (<768px): AVIF → WebP → PNG fallback. */}
            <source type="image/avif" srcSet={HERO_MOBILE.avif} />
            <source type="image/webp" srcSet={HERO_MOBILE.webp} />
            <img
              src={HERO_MOBILE.png}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              fetchPriority="high"
            />
          </picture>
          {/* Subtle bottom fade only — the source image already carries its
              own dark tones, so we skip the heavier vignettes we use for
              the trailer stack to keep the artwork clean. */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink/70 to-transparent" />

          {/* Rotating headline — cycles "Real Stories." → "Bold Voices."
              → "Africa." one word at a time under the poster so the hero
              feels alive during the 9s static reveal. Removed the moment
              the poster hands off to the trailer stack (opacity gate via
              parent). Sits inside the poster layer so it fades away
              together. */}
          <div
            className="absolute inset-x-0 bottom-24 sm:bottom-16 flex items-center justify-center pointer-events-none"
            data-testid="hero-rotating-headline"
            aria-hidden={heroRevealed}
          >
            <div className="flex items-baseline gap-2 px-4 py-2 rounded-full bg-black/40 backdrop-blur-sm border border-white/10">
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-terracotta font-semibold">Now streaming</span>
              <span
                key={heroWordIdx}
                className="font-display text-sm sm:text-base font-bold text-foreground animate-in fade-in slide-in-from-bottom-1 duration-500"
                data-testid="hero-rotating-word"
              >
                {HERO_ROTATING_WORDS[heroWordIdx]}
              </span>
            </div>
          </div>
        </div>

        {/* BACKDROP STACK — one slide per film, crossfades */}
        {heroFilms.length === 0 ? (
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/1502311/pexels-photo-1502311.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
              alt=""
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-terracotta/10 via-ink/40 to-ink" />
          </div>
        ) : (
          heroFilms.map((f, i) => (
            <div
              key={f.id}
              className="absolute inset-0 transition-opacity duration-[900ms] ease-in-out"
              style={{ opacity: i === heroIdx ? 1 : 0 }}
              aria-hidden={i !== heroIdx}
            >
              {/* Branded ambient base — always visible behind the video */}
              <div className="absolute inset-0 bg-gradient-to-br from-terracotta/15 via-ink to-ink" />

              {/* Poster fallback ONLY when this film has no trailer — otherwise
                  we go straight from the ambient gradient into the trailer
                  without any static-image flash. */}
              {!hasTrailer(f) && (
                <img
                  src={`${API}/films/${f.id}/poster`}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-65"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}

              {/* Trailer video — autoplays muted, loops, fills the frame.
                  Only the active slide plays (see effect above). */}
              {hasTrailer(f) && (
                <StreamVideo
                  ref={(el) => { videoRefs.current[i] = el; }}
                  playbackPath={`/films/${f.id}/trailer-playback`}
                  legacySrc={f.trailer_path ? `${API}/films/${f.id}/trailer${f.trailer_uploaded_at ? `?v=${encodeURIComponent(f.trailer_uploaded_at)}` : ""}` : null}
                  className="absolute inset-0 w-full h-full object-cover"
                  muted={muted}
                  loop
                  playsInline
                  preload={i === heroIdx ? "auto" : "none"}
                  // Hide native UI completely — this is decorative playback.
                  controls={false}
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid={`hero-trailer-${i}`}
                />
              )}

              {/* Gradient stack — bottom-up for text legibility,
                  left-to-right for the title side, plus a vignette top. */}
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 via-40% to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/40 to-transparent" />
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-ink/70 to-transparent" />
            </div>
          ))
        )}

        {/* FOREGROUND — title, synopsis, CTAs */}
        <div
          className={`relative z-30 h-full px-6 sm:px-12 lg:px-20 max-w-[1600px] mx-auto flex items-end pb-20 sm:pb-24 lg:pb-28 transition-opacity duration-[900ms] ease-in-out ${
            heroRevealed ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="max-w-2xl space-y-4 sm:space-y-5" data-testid="hero-content">
            {current ? (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-terracotta/20 backdrop-blur-sm border border-terracotta/40">
                  <Sparkles className="w-3.5 h-3.5 text-gold" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-sand font-medium">
                    Now showing on Cinevoya
                  </span>
                </div>

                <h1
                  className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight uppercase drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]"
                  data-testid="hero-film-title"
                >
                  {current.title}
                </h1>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-foreground/85">
                  <span className="px-2 py-0.5 rounded bg-gold/20 backdrop-blur-sm text-gold text-[11px] font-bold tracking-widest uppercase border border-gold/40">
                    {current.genre}
                  </span>
                  <span className="flex items-center gap-1.5 text-foreground/80">
                    <Clock className="w-3.5 h-3.5" /> {current.duration_minutes} min
                  </span>
                  {current.year && <span className="text-foreground/65">· {current.year}</span>}
                  {current.country && <span className="text-foreground/65">· {current.country}</span>}
                  <span className="text-foreground/65">· by {current.filmmaker_name}</span>
                </div>

                <p className="text-base sm:text-lg text-foreground/85 leading-relaxed line-clamp-2 sm:line-clamp-3 max-w-xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                  {current.synopsis}
                </p>

                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Button
                    onClick={() => navigate(`/film/${current.id}`)}
                    className="bg-white text-ink hover:bg-sand h-12 px-7 text-base font-semibold shadow-xl shadow-black/50"
                    data-testid="hero-watch-btn"
                  >
                    <Play className="w-4 h-4 fill-ink" /> Rent · {naira(current.price_naira)}
                  </Button>
                  <Button
                    onClick={() => navigate(`/film/${current.id}`)}
                    variant="outline"
                    size="icon"
                    className="border-white/30 bg-black/40 hover:bg-black/60 backdrop-blur-sm h-12 w-12 rounded-full text-white"
                    aria-label="More info"
                    data-testid="hero-info-btn"
                  >
                    <Info className="w-5 h-5" />
                  </Button>
                  {hasTrailer(current) && (
                    <Button
                      onClick={() => setMuted((m) => !m)}
                      variant="outline"
                      size="icon"
                      className="border-white/30 bg-black/40 hover:bg-black/60 backdrop-blur-sm h-12 w-12 rounded-full text-white ml-auto"
                      aria-label={muted ? "Unmute trailer" : "Mute trailer"}
                      data-testid="hero-mute-btn"
                    >
                      {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              // Empty-catalogue fallback — keeps the brand promise visible.
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-terracotta/15 border border-terracotta/30">
                  <Sparkles className="w-3.5 h-3.5 text-gold" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-sand font-medium">Africa's short film stage</span>
                </div>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95]">
                  Stories that <span className="text-gradient-warm">echo a continent</span>.
                </h1>
                <p className="text-base sm:text-lg text-foreground/75 leading-relaxed max-w-xl">
                  Curated pay-per-view shorts from <span className="text-gold font-semibold">₦100</span>. Filmmakers earn, audiences discover.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button onClick={() => navigate("/browse")} className="bg-white text-ink hover:bg-sand h-12 px-7 text-base font-semibold" data-testid="hero-browse-btn">
                    <Play className="w-4 h-4 fill-ink" /> Watch shorts
                  </Button>
                  <Button onClick={() => navigate("/register")} variant="outline" className="border-white/25 bg-white/10 hover:bg-white/15 backdrop-blur text-white h-12 px-6">
                    I'm a filmmaker <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* CHEVRON ARROWS */}
        {heroLen > 1 && heroRevealed && (
          <>
            <button
              onClick={goPrev}
              className="hidden sm:flex absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur border border-white/15 items-center justify-center text-white/85 hover:text-white transition-all hover:scale-105"
              aria-label="Previous film"
              data-testid="hero-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="hidden sm:flex absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur border border-white/15 items-center justify-center text-white/85 hover:text-white transition-all hover:scale-105"
              aria-label="Next film"
              data-testid="hero-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* SLIDE INDICATORS */}
        {heroLen > 1 && heroRevealed && (
          <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" data-testid="hero-indicators">
            {heroFilms.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === heroIdx ? "w-8 bg-white" : "w-2 bg-white/35 hover:bg-white/55"
                }`}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-indicator-${i}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* FEATURED ROW */}
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-[1600px] mx-auto" data-testid="featured-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Now showing</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Fresh from filmmakers</h2>
          </div>
          <Link to="/browse" className="text-sm text-foreground/70 hover:text-terracotta transition-colors flex items-center gap-1">
            See all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {films.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="aspect-[2/3] rounded-lg shimmer-bg animate-shimmer border border-white/5" />
            ))}
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-4 -mx-6 px-6">
            {films.map((f, i) => (
              <FilmCard key={f.id} film={f} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* PREMIERING STRIP — rendered above Monthly Spotlight. Shows
          every film the filmmaker has flagged as premiering, newest
          first, in a horizontally scrollable row styled consistently
          with the Spotlight strip below. Hides itself if nothing is
          flagged so empty landings never show a stub. */}
      {premiering.length > 0 && (
        <section className="px-6 sm:px-12 lg:px-20 pt-16 max-w-[1600px] mx-auto" data-testid="premiering-section">
          <div className="rounded-2xl p-8 sm:p-12 bg-gradient-to-br from-terracotta/15 via-ember/5 to-transparent border border-terracotta/30 relative overflow-hidden">
            <div className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-terracotta/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Premiering
                  </p>
                  <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                    Fresh <span className="text-gradient-warm">premieres</span> from our filmmakers.
                  </h2>
                  <p className="text-foreground/60 mt-2 text-sm">Short films debuting now — hand-picked by their creators.</p>
                </div>
              </div>

              <div
                className="flex gap-5 overflow-x-auto pb-3 -mr-6 sm:-mr-12 lg:-mr-20 snap-x snap-mandatory [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-terracotta/40 [&::-webkit-scrollbar-thumb]:rounded-full"
                data-testid="premiering-scroller"
              >
                {premiering.map((f) => (
                  <Link
                    key={f.id}
                    to={`/film/${f.id}`}
                    className="group shrink-0 snap-start w-[64vw] sm:w-[280px] md:w-[300px] lg:w-[260px]"
                    data-testid={`premiering-card-${f.id}`}
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden border border-white/10 bg-surface mb-3">
                      <img src={`${API}/films/${f.id}/poster`} alt={f.title} onError={(e)=>{e.currentTarget.style.opacity=0.3}} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-terracotta text-white font-display font-black text-[11px] tracking-wider uppercase flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Premiere
                      </div>
                      <AiBadge film={f} className="absolute top-3 right-3" />
                    </div>
                    <h3 className="font-display font-bold text-lg leading-tight group-hover:text-terracotta transition-colors line-clamp-1">{f.title}</h3>
                    <p className="text-xs text-foreground/60 line-clamp-1">{f.filmmaker_name} · {f.genre}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* COMING SOON STRIP — flagged films with an announced release
          date. Not watchable yet; the badge + date communicate "save
          the date". Hides itself when the shelf is empty. */}
      {comingSoon.length > 0 && (
        <section className="px-6 sm:px-12 lg:px-20 pt-16 max-w-[1600px] mx-auto" data-testid="coming-soon-section">
          <div className="rounded-2xl p-8 sm:p-12 bg-gradient-to-br from-sky-500/10 via-indigo-500/5 to-transparent border border-sky-400/20 relative overflow-hidden">
            <div className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-sky-300 font-semibold mb-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Coming Soon
                  </p>
                  <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                    New <span className="text-sky-300">releases</span> on the way.
                  </h2>
                  <p className="text-foreground/60 mt-2 text-sm">Short films arriving soon — mark your calendar.</p>
                </div>
              </div>

              <div
                className="flex gap-5 overflow-x-auto pb-3 -mr-6 sm:-mr-12 lg:-mr-20 snap-x snap-mandatory [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-sky-400/40 [&::-webkit-scrollbar-thumb]:rounded-full"
                data-testid="coming-soon-scroller"
              >
                {comingSoon.map((f) => (
                  <Link
                    key={f.id}
                    to={`/film/${f.id}`}
                    className="group shrink-0 snap-start w-[64vw] sm:w-[280px] md:w-[300px] lg:w-[260px]"
                    data-testid={`coming-soon-card-${f.id}`}
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden border border-white/10 bg-surface mb-3">
                      <img src={`${API}/films/${f.id}/poster`} alt={f.title} onError={(e) => { e.currentTarget.style.opacity = 0.3; }} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-sky-500 text-white font-display font-black text-[11px] tracking-wider uppercase flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Soon
                      </div>
                      <AiBadge film={f} className="absolute top-3 right-3" />
                      {f.release_date && (
                        <div className="absolute bottom-3 left-3 right-3 px-3 py-1 rounded-md bg-black/60 backdrop-blur text-[11px] text-white/90 font-semibold text-center" data-testid={`coming-soon-date-${f.id}`}>
                          {new Date(String(f.release_date).slice(0, 10) + "T12:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                        </div>
                      )}
                    </div>
                    <h3 className="font-display font-bold text-lg leading-tight group-hover:text-sky-300 transition-colors line-clamp-1">{f.title}</h3>
                    <p className="text-xs text-foreground/60 line-clamp-1">{f.filmmaker_name} · {f.genre}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <AiFilmsRow />

      {/* MONTHLY SPOTLIGHT */}
      {spotlight.films.length > 0 && (
        <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-[1600px] mx-auto" data-testid="spotlight-section">
          <div className="rounded-2xl p-8 sm:p-12 bg-gradient-to-br from-gold/15 via-terracotta/5 to-transparent border border-gold/30 relative overflow-hidden">
            <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-2 flex items-center gap-1.5">
                    <Trophy className="w-3 h-3" />
                    {spotlight.auto_selected ? "Fresh on Cinevoya" : `Monthly spotlight · ${spotlight.month}`}
                  </p>
                  <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                    {spotlight.auto_selected ? (
                      <>The newest <span className="text-gradient-warm">short films</span>.</>
                    ) : (
                      <>Top 5 short films <span className="text-gradient-warm">this month</span>.</>
                    )}
                  </h2>
                </div>
                <p className="text-xs text-foreground/60 max-w-xs">
                  {spotlight.auto_selected
                    ? "The latest approved shorts on the platform — straight from our newest filmmakers."
                    : "Hand-picked by our curators. Recognition, audience reach, and small prizes for the makers."}
                </p>
              </div>
              {/* Horizontally scrollable row — scroll-snap keeps each
                  card nicely aligned to the gutter on swipe. Padded
                  trailing space (last-child mr) gives a "there's more
                  over here" affordance on the final card. */}
              <div
                className="flex gap-5 overflow-x-auto pb-3 -mr-6 sm:-mr-12 lg:-mr-20 snap-x snap-mandatory [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-gold/40 [&::-webkit-scrollbar-thumb]:rounded-full"
                data-testid="spotlight-scroller"
              >
                {spotlight.films.map((f) => (
                  <Link
                    key={f.id}
                    to={`/film/${f.id}`}
                    className="group shrink-0 snap-start w-[64vw] sm:w-[280px] md:w-[300px] lg:w-[260px]"
                    data-testid={`spotlight-card-${f.spotlight_rank}`}
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden border border-white/10 bg-surface mb-3">
                      <img src={`${API}/films/${f.id}/poster`} alt={f.title} onError={(e)=>{e.currentTarget.style.opacity=0.3}} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-gold text-ink font-display font-black text-sm flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-ink" /> #{f.spotlight_rank}
                      </div>
                      <AiBadge film={f} className="absolute top-3 right-3" />
                    </div>
                    <h3 className="font-display font-bold text-lg leading-tight group-hover:text-gold transition-colors line-clamp-1">{f.title}</h3>
                    <p className="text-xs text-foreground/60 line-clamp-1">{f.filmmaker_name} · {f.genre}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-[1600px] mx-auto" data-testid="how-it-works">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">How it works</p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold">A platform built for African storytellers.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Film, title: "Upload your short", body: "Submit your film with a synopsis, themes, and price between ₦100–₦300. Our team reviews quickly." },
            { icon: CreditCard, title: "Earn per view", body: "Audiences pay-per-view via Paystack. You earn directly while we handle hosting, payments and discovery." },
            { icon: Award, title: "AI festival match", body: "Claude analyses every film and tells you which festivals it qualifies for — then we help you submit." },
          ].map((item, i) => (
            <div
              key={item.title}
              className="p-8 rounded-xl bg-surface border border-white/10 hover:border-terracotta/40 transition-colors group"
              data-testid={`how-it-works-card-${i}`}
            >
              <div className="w-12 h-12 rounded-lg bg-terracotta/15 border border-terracotta/30 flex items-center justify-center mb-5 group-hover:bg-terracotta/25 transition-colors">
                <item.icon className="w-5 h-5 text-terracotta" />
              </div>
              <h3 className="font-display text-xl font-bold mb-2">{item.title}</h3>
              <p className="text-foreground/70 text-sm leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-[1600px] mx-auto" data-testid="landing-cta">
        <div className="rounded-2xl p-8 sm:p-16 bg-gradient-to-br from-terracotta/20 via-ember/10 to-transparent border border-terracotta/30 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-gold/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-5xl font-bold mb-4">
              Your film deserves a stage.
            </h2>
            <p className="text-foreground/75 mb-8 text-base sm:text-lg">
              Join filmmakers from Lagos to Cape Town. Upload, earn, and let our AI plot your festival map.
            </p>
            <Button
              onClick={() => navigate("/register")}
              className="bg-terracotta hover:bg-ember text-white h-12 px-8 text-base"
              data-testid="cta-register-btn"
            >
              Start as a filmmaker
            </Button>
          </div>
        </div>
      </section>

      {/* MOBILE APP — teaser for the upcoming iOS + Android apps. The image is
          a floating-iPhone mockup; we keep the layout responsive so the phone
          collapses under the copy on mobile screens instead of shrinking to
          nothing. */}
      <section className="px-6 sm:px-12 lg:px-20 py-20 border-t border-white/5 overflow-hidden" data-testid="mobile-app-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="rounded-3xl border border-gold/20 bg-gradient-to-br from-terracotta/10 via-surface to-ink relative overflow-hidden">
            <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
            <div className="absolute -right-40 -bottom-40 w-[520px] h-[520px] rounded-full bg-terracotta/15 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="p-10 sm:p-14 lg:p-16">
                <p className="text-xs uppercase tracking-[0.24em] text-gold font-semibold mb-4 flex items-center gap-2">
                  <Smartphone className="w-3.5 h-3.5" /> {isStandalone ? "Installed" : "Progressive Web App"}
                </p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4">
                  Cinevoya on <span className="text-gradient-warm">Mobile</span>
                </h2>
                <p className="text-foreground/70 text-base sm:text-lg mb-8 max-w-lg">
                  Take Cinevoya with you. Install Cinevoya on your phone for an app-like experience and watch African stories anytime, anywhere.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      if (isStandalone) return;
                      const outcome = await install();
                      if (outcome === "unavailable") {
                        toast.info("Open Cinevoya in Chrome, Edge or Safari on your phone to install it as an app.");
                      } else if (outcome === "dismissed") {
                        toast("You can install Cinevoya any time from this button.");
                      } else if (outcome === "accepted") {
                        toast.success("Cinevoya added to your home screen.");
                      }
                    }}
                    disabled={isStandalone}
                    className="group inline-flex items-center gap-3 px-6 h-14 rounded-xl bg-terracotta text-white shadow-xl shadow-terracotta/30 hover:bg-terracotta/90 transition-all disabled:bg-surface disabled:text-foreground/70 disabled:border disabled:border-white/10 disabled:shadow-none disabled:cursor-default"
                    data-testid="install-cinevoya-btn"
                    aria-label={isStandalone ? "Cinevoya is installed" : "Install Cinevoya on this device"}
                  >
                    {isStandalone ? (
                      <>
                        <Check className="w-5 h-5" />
                        <div className="leading-tight text-left">
                          <p className="text-[10px] uppercase tracking-widest opacity-70">You&apos;re all set</p>
                          <p className="font-display font-semibold text-lg">Cinevoya Installed</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
                        <div className="leading-tight text-left">
                          <p className="text-[10px] uppercase tracking-widest opacity-80">
                            {platform === "ios-safari" ? "iOS · Add to Home Screen" : platform === "ios-chrome" ? "iOS · Open in Safari" : "One-tap install"}
                          </p>
                          <p className="font-display font-semibold text-lg">Install Cinevoya</p>
                        </div>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-foreground/45 mt-6">
                  Works offline for your library and rentals. No app store needed.
                </p>
              </div>
              <div className="relative flex items-center justify-center py-2 sm:py-6 lg:py-0 lg:min-h-[500px] px-0 lg:px-8">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/wingman/d3d23305-bd96-49e0-a6a1-054251ff8d67/attachments/805cac4041964dfabc667c03d4409df7_floating-iphones-14-pro-max-removebg-preview.png"
                  alt="Cinevoya iPhone app preview"
                  className="max-w-none lg:max-w-full w-[130%] sm:w-[125%] md:w-[120%] lg:w-full h-auto object-contain drop-shadow-[0_30px_60px_rgba(195,106,74,0.35)] animate-float scale-110 md:scale-125 lg:scale-100"
                  data-testid="mobile-app-hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT — sits above the footer, gives prospective filmmakers a
          direct line to the team without hunting through menus. */}
      <section className="px-6 sm:px-12 lg:px-20 py-16 border-t border-white/5" data-testid="landing-contact">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 md:gap-12 items-end">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">Reach the curation desk</p>
              <h2 className="font-display text-3xl sm:text-4xl font-black leading-tight">
                Questions, submissions or partnerships?
              </h2>
              <p className="text-foreground/65 mt-2 text-sm sm:text-base max-w-xl">
                Our team reads every email and answers within 48 hours.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <a
                href="mailto:Hello@cinevoya.com"
                className="group inline-flex items-center gap-3 px-5 py-3.5 rounded-lg border border-white/10 bg-surface hover:bg-white/5 hover:border-terracotta/40 transition-colors"
                data-testid="contact-email-link"
              >
                <span className="w-9 h-9 rounded-full bg-terracotta/15 border border-terracotta/30 flex items-center justify-center text-terracotta group-hover:bg-terracotta/25 transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/45">Email</p>
                  <p className="text-sm font-medium text-foreground group-hover:text-terracotta transition-colors">Hello@cinevoya.com</p>
                </div>
              </a>
              <a
                href="mailto:moses@cinevoya.com"
                className="group inline-flex items-center gap-3 px-5 py-3.5 rounded-lg border border-white/10 bg-surface hover:bg-white/5 hover:border-terracotta/40 transition-colors"
                data-testid="contact-email-moses-link"
              >
                <span className="w-9 h-9 rounded-full bg-terracotta/15 border border-terracotta/30 flex items-center justify-center text-terracotta group-hover:bg-terracotta/25 transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/45">Email</p>
                  <p className="text-sm font-medium text-foreground group-hover:text-terracotta transition-colors">moses@cinevoya.com</p>
                </div>
              </a>
              <a
                href="tel:+2347069770897"
                className="group inline-flex items-center gap-3 px-5 py-3.5 rounded-lg border border-white/10 bg-surface hover:bg-white/5 hover:border-gold/40 transition-colors"
                data-testid="contact-phone-link"
              >
                <span className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold/25 transition-colors">
                  <Phone className="w-4 h-4" />
                </span>
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-widest text-foreground/45">Call · WhatsApp</p>
                  <p className="text-sm font-medium text-foreground group-hover:text-gold transition-colors tabular-nums">070 6977 0897</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
